import React, { useCallback, useContext, useEffect, useState } from 'react';
import styled from 'styled-components';
import { useBookingContext } from '../../Context';
import { useFetchCalEvents } from '../../hooks/useCalEvents';
import { BookerStateContext } from '../../hooks/useRuntimeCalBookingState';
import { CalEventData, DesignStyle } from '../../types';
import EventCard from './EventCard';
import EventListSkeleton from './EventListSkeleton';
import { EventsList } from './styles';

const SelectEventStep = () => {
    const bookerContext = useBookingContext();
    const [, bookingActions] = useContext(BookerStateContext);
    const [calEventsMap, setCalEventsMap] = useState<Map<string, CalEventData>>(
        () => new Map()
    );
    const { isLoading, fetchCalEvents } = useFetchCalEvents(bookerContext);

    const { events, organizationId, teamId, groupedAppointmentInfo } =
        bookerContext;

    const shouldFetchEvents = Boolean(
        organizationId && teamId && events.length > 0
    );

    const isFetchingEvents = isLoading && shouldFetchEvents;

    const eventsListLabel =
        groupedAppointmentInfo?.title || 'Available services';

    const handleEventSelect = useCallback(
        (eventSlug: string) => {
            if (isLoading) {
                return;
            }

            bookingActions.setIsLoading(true);
            bookingActions.setEventSlug(eventSlug);
        },
        [isLoading, bookingActions]
    );

    useEffect(() => {
        // Early return if conditions are not met
        if (!shouldFetchEvents) {
            return;
        }

        fetchCalEvents({
            organizationId: organizationId!,
            teamId: teamId!,
        }).then((calEvents) => {
            // Map of calEventId to identifier
            const calEventIdToIdentifierMap = new Map(
                events.map((event) => [event.calEventId, event.identifier])
            );

            const map = new Map<string, CalEventData>();
            calEvents?.forEach((event) => {
                if (calEventIdToIdentifierMap.has(event.id)) {
                    map.set(calEventIdToIdentifierMap.get(event.id)!, event);
                }
            });

            setCalEventsMap(map);
        });

        // we only want to fetch events once when the component mounts
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    return (
        <OuterContainer className='booking-select-events-step'>
            <Container>
                {groupedAppointmentInfo?.isTitleVisible &&
                    groupedAppointmentInfo.title && (
                        <GroupedEventsTitle data-auto='booking-select-events-step-grouped-events-title'>
                            {groupedAppointmentInfo.title}
                        </GroupedEventsTitle>
                    )}
                {isFetchingEvents ? (
                    <EventListSkeleton
                        ariaLabel={eventsListLabel}
                        dataAuto='booking-select-events-step-events-list-skeleton'
                    />
                ) : (
                    <EventsList
                        aria-label={eventsListLabel}
                        data-auto='booking-select-events-step-events-list'
                    >
                        {events.map((event) => (
                            <EventCard
                                key={event.identifier}
                                {...event}
                                calEventData={calEventsMap.get(
                                    event.identifier
                                )}
                                onSelect={handleEventSelect}
                                isLoading={isLoading}
                            />
                        ))}
                    </EventsList>
                )}
            </Container>
        </OuterContainer>
    );
};

const OuterContainer = styled.div`
    display: flex;
    padding: 32px;
    width: 100%;
    min-height: 450px;
    border-width: 1px;
    border-style: solid;
    border-radius: 6px;

    // default
    background-color: #fff;
    border-color: #e5e7eb;
    color: rgb(60, 62, 68);

    .${DesignStyle.SITE_THEME} & {
        border-color: var(--color_1);
        background-color: var(--color_3);
        color: var(--color_1);
    }
`;

const Container = styled.div`
    max-width: 820px;
    width: 100%;
    margin: 0 auto;
    overflow: auto;
    margin: 0 auto;
`;

const GroupedEventsTitle = styled.div`
    font-weight: 550;
    font-size: 20px;
    line-height: 26px;
    letter-spacing: 0px;
    margin-bottom: 32px;
    color: #272b35;

    .${DesignStyle.SITE_THEME} & {
        color: var(--color_1);
    }
`;

export default SelectEventStep;
